import { useEffect, useMemo, useState } from "react";
import { useTextos } from "../context/useTextos";
import { useWorkshop } from "../context/useWorkshop";
import { checklistService } from "../services/checklistService";
import { clienteService } from "../services/cadastroService";
import { CHECKLIST_STATUS, CHECKLIST_STATUS_LABELS } from "../lib/checklistTemplate";
import { contarCriticosChecklist } from "../lib/criterios";
import StatusBadge from "../components/StatusBadge";

const LOCALE_POR_IDIOMA = { pt: "pt-BR", en: "en-US", es: "es-ES" };
const CHECKLIST_STATUS_I18N_KEYS = {
  rascunho: "checklist_status_rascunho",
  concluido: "checklist_status_concluido",
  aprovado: "checklist_status_aprovado",
};

function toInputDate(date) {
  return date.toISOString().slice(0, 10);
}

function dentroDoPeriodo(iso, inicio, fim) {
  const data = new Date(iso);
  if (inicio && data < new Date(`${inicio}T00:00:00`)) return false;
  if (fim && data > new Date(`${fim}T23:59:59`)) return false;
  return true;
}

function defaultInicio() {
  const d = new Date();
  d.setDate(1);
  return toInputDate(d);
}

function csvEscape(value) {
  const str = String(value ?? "");
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

export default function RelatoriosPage({ onAbrirChecklist }) {
  const { t, i18n } = useTextos();
  const localeAtual = LOCALE_POR_IDIOMA[i18n.language] || "pt-BR";
  const { workshopId } = useWorkshop();
  const [inicio, setInicio] = useState(defaultInicio());
  const [fim, setFim] = useState(toInputDate(new Date()));
  const [clienteId, setClienteId] = useState("");
  const [status, setStatus] = useState("");

  const [checklists, setChecklists] = useState([]);
  useEffect(() => {
    checklistService.list(workshopId).then(setChecklists);
  }, [workshopId]);
  const [clientes, setClientes] = useState([]);
  useEffect(() => {
    clienteService.list(workshopId).then(setClientes);
  }, [workshopId]);

  const filtrados = useMemo(() => {
    return checklists.filter((c) => {
      if (!dentroDoPeriodo(c.createdAt, inicio, fim)) return false;
      if (clienteId && c.clienteId !== clienteId) return false;
      if (status && c.status !== status) return false;
      return true;
    });
  }, [checklists, inicio, fim, clienteId, status]);

  const resumo = useMemo(() => {
    const aprovados = filtrados.filter((c) => c.status === CHECKLIST_STATUS.APROVADO).length;
    const itensCriticos = filtrados.reduce((total, c) => total + contarCriticosChecklist(c), 0);
    return { total: filtrados.length, aprovados, itensCriticos };
  }, [filtrados]);

  function exportarCsv() {
    const linhas = [
      [t("checklist.col_data"), t("checklist.col_cliente"), t("checklist.col_veiculo"), t("checklist.col_placa"), t("checklist.col_status"), t("checklist.col_itens_criticos")].map(csvEscape).join(","),
      ...filtrados.map((c) =>
        [
          new Date(c.createdAt).toLocaleDateString(localeAtual),
          c.cliente?.nome || "",
          `${c.veiculo?.marca || ""} ${c.veiculo?.modelo || ""}`.trim(),
          c.veiculo?.placa || "",
          CHECKLIST_STATUS_I18N_KEYS[c.status] ? t(`checklist.${CHECKLIST_STATUS_I18N_KEYS[c.status]}`) : (CHECKLIST_STATUS_LABELS[c.status] || c.status),
          contarCriticosChecklist(c),
        ]
          .map(csvEscape)
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob(["\uFEFF", linhas], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `relatorio-checklists-${inicio}-a-${fim}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="page">
      <header className="page__header">
        <h2>{t("checklist.rel_titulo")}</h2>
        <button className="btn btn--secondary" onClick={exportarCsv} disabled={filtrados.length === 0}>
          {t("checklist.rel_exportar_csv")}
        </button>
      </header>

      <section className="form-section">
        <h3>{t("checklist.rel_filtros")}</h3>
        <div className="form-grid">
          <label>
            {t("checklist.rel_de")}
            <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} />
          </label>
          <label>
            {t("checklist.rel_ate")}
            <input type="date" value={fim} onChange={(e) => setFim(e.target.value)} />
          </label>
          <label>
            {t("checklist.rel_cliente")}
            <select value={clienteId} onChange={(e) => setClienteId(e.target.value)}>
              <option value="">{t("checklist.rel_todos")}</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t("checklist.rel_status")}
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{t("checklist.rel_todos")}</option>
              {Object.keys(CHECKLIST_STATUS_LABELS).map((value) => (
                <option key={value} value={value}>
                  {t(`checklist.${CHECKLIST_STATUS_I18N_KEYS[value]}`)}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <div className="kpi-grid">
        <div className="kpi-card">
          <span className="kpi-card__label">{t("checklist.rel_kpi_periodo")}</span>
          <span className="kpi-card__value">{resumo.total}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-card__label">{t("checklist.rel_kpi_aprovados")}</span>
          <span className="kpi-card__value">{resumo.aprovados}</span>
        </div>
        <div className="kpi-card kpi-card--critico">
          <span className="kpi-card__label">{t("checklist.rel_kpi_criticos")}</span>
          <span className="kpi-card__value">{resumo.itensCriticos}</span>
        </div>
      </div>

      <section className="form-section">
        <h3>{t("checklist.rel_lista_titulo")}</h3>
        {filtrados.length === 0 ? (
          <p className="empty-state">{t("checklist.rel_nenhum")}</p>
        ) : (
          <div className="table-scroll">
            <table className="checklist-table">
              <thead>
                <tr>
                  <th>{t("checklist.col_data")}</th>
                  <th>{t("checklist.col_cliente")}</th>
                  <th>{t("checklist.col_veiculo")}</th>
                  <th>{t("checklist.col_placa")}</th>
                  <th>{t("checklist.col_status")}</th>
                  <th>{t("checklist.col_itens_criticos")}</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map((c) => (
                  <tr key={c.id} className="checklist-table__row" onClick={() => onAbrirChecklist?.(c.id)}>
                    <td>{new Date(c.createdAt).toLocaleDateString(localeAtual)}</td>
                    <td>{c.cliente?.nome || "—"}</td>
                    <td>
                      {c.veiculo?.marca} {c.veiculo?.modelo}
                    </td>
                    <td>{c.veiculo?.placa || "—"}</td>
                    <td>
                      <StatusBadge status={c.status} kind="checklist" />
                    </td>
                    <td>{contarCriticosChecklist(c)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
